
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useCategories from '@/hooks/useCategories';

interface InsightsFiltersProps {
  onCategoryChange: (category: string | null) => void;
  onSearchChange: (search: string) => void;
  selectedCategory: string | null;
  searchQuery: string;
}

const InsightsFilters = ({
  onCategoryChange,
  onSearchChange,
  selectedCategory,
  searchQuery
}: InsightsFiltersProps) => {
  const { categories } = useCategories();
  const [search, setSearch] = useState(searchQuery);
  
  // Apply search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(search);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [search, onSearchChange]);

  // Get selected category name for display
  const getSelectedCategoryName = () => {
    if (!selectedCategory) return 'All Categories';
    const category = categories.find(cat => cat.id === selectedCategory);
    return category ? category.name : 'All Categories';
  };

  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search insights..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category Filter Dropdown */}
        <div className="flex items-center gap-2">
          <div className="text-nation-blue dark:text-blue-300 flex items-center">
            <Filter size={18} className="mr-1" />
            <span className="hidden sm:inline">Filter:</span>
          </div>
          
          <Select value={selectedCategory || 'all'} onValueChange={(value) => onCategoryChange(value === 'all' ? null : value)}>
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="Select category">
                {getSelectedCategoryName()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default InsightsFilters;
